import axios from '@axios'
import { getCachedData, setCachedData, TTL } from '@/helpers/cache-api'
import requestSharedSettings from '@/helpers/settings-request'

export default {
  namespaced: true,
  state: {
    eventStatuses: null,
    rentalPeriods: null,
    places: null,
    halls: null,
    cities: null,
    shows: null,
    priceZones: null,
    clientTypes: null,
  },
  getters: {
    eventStatuses(state) {
      return state.eventStatuses
    },
    rentalPeriods(state) {
      return state.rentalPeriods
    },
    places(state) {
      return state.places
    },
    halls(state) {
      return state.halls
    },
    cities(state) {
      return state.cities
    },
    shows(state) {
      return state.shows
    },
    priceZones(state) {
      return state.priceZones
    },
    clientTypes(state) {
      return state.clientTypes
    },
  },
  mutations: {
    SET_EVENT_STATUSES(state, statuses) {
      state.eventStatuses = statuses
    },
    SET_RENTAL_PERIODS(state, periods) {
      state.rentalPeriods = periods
    },
    SET_PLACES(state, places) {
      state.places = places
    },
    SET_HALLS(state, halls) {
      state.halls = halls
    },
    SET_CITIES(state, cities) {
      state.cities = cities
    },
    SET_SHOWS(state, shows) {
      state.shows = shows
    },
    SET_PRICE_ZONES(state, priceZones) {
      state.priceZones = priceZones
    },
    SET_CLIENT_TYPES(state, data) {
      state.clientTypes = data
    },
  },
  actions: {
    // Users
    logout() {
      return new Promise((resolve, reject) => {
        axios
          .get('/auth/logout', { skipAuthRefresh: true })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Users
    fetchUsersByRole(ctx, {name}) {
      const actionName = 'fetchUsersByRole'
      const cachedData = getCachedData(actionName, {name}, TTL.days(7))

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get(`/users-by-role/${name}`)
          .then(response => {
            setCachedData(actionName, {name}, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    // Cities
    fetchCities(ctx, queryParams) {
      if (ctx.state.cities !== null && (Object.keys(queryParams ?? {}).length === 0)) {
        return new Promise(resolve => resolve({data: {data: ctx.state.cities}}))
      }
      return new Promise((resolve, reject) => {
        axios
          .get('/cities/', {params: queryParams})
          .then(response => {
            ctx.commit('SET_CITIES', response.data.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    addCity(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/cities/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getCity(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/cities/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateCity(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/cities/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Reviews
    fetchReviews(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/reviews/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addReview(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/reviews/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getReview(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/reviews/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateReview(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/reviews/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    reviewsSetStatus(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/reviews/${queryParams.id}/set-status/${queryParams.status}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Places
    fetchPlaces(ctx, queryParams) {
      const actionName = 'fetchPlaces'
      const cachedData = getCachedData(actionName, queryParams, TTL.days(7))

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/places/', {params: queryParams})
          .then(response => {
            // ctx.commit('SET_PLACES', response.data.data)
            setCachedData(actionName, queryParams, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    places(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/places/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addPlace(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/places/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getPlace(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/places/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchPlaceSurveyQr(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/places/${id}/survey/qr`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    downloadPlaceSurveyQr(ctx, {id, format}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/places/${id}/survey/qr-download`, {
            params: {format},
            responseType: 'blob',
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updatePlace(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/places/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    placesEasyList(ctx, queryParams) {
      const actionName = 'placesEasyList'
      const cachedData = getCachedData(actionName, queryParams)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/placesEasyList', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },

    // Halls
    fetchHalls(ctx, queryParams) {
      const actionName = 'fetchHalls'
      const cachedData = getCachedData(actionName, queryParams)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/halls/', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    addHall(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/halls/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getHall(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/halls/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateHall(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/halls/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    copyHall(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/halls/${id}/copy`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getHallMap(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/halls/${queryParams.hallId}/map`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    storeHallMap(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/halls/${queryParams.hallId}/map`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    checkHallSeats(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/halls/map/check-seats', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getMapHistory(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/halls/${queryParams.hallId}/history`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    restoreMap(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/halls/${queryParams.hallId}/history/${queryParams.id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Shows
    fetchShows(ctx, queryParams) {
      const actionName = 'fetchShows'
      const cachedData = getCachedData(actionName, queryParams)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/shows/', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    shows(ctx, queryParams) {
      const actionName = 'shows'
      const cachedData = getCachedData(actionName, queryParams)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/shows/', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    // Shows
    fetchListingShows(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/fetchListingShows/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchShowsWithID(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/fetchShowsWithID/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addShow(ctx, queryParams) {
      const formData = new FormData()
      Object.keys(queryParams).forEach(key => formData.append(key, queryParams[key]))
      return new Promise((resolve, reject) => {
        axios
          .post('/shows/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getShow(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/shows/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateShow(ctx, queryParams) {
      const formData = new FormData()
      Object.keys(queryParams).forEach(key => formData.append(key, queryParams[key]))
      return new Promise((resolve, reject) => {
        axios
          .post(`/shows/${queryParams.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Events
    fetchEvents(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/events/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getSimilar(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`events/${id}/similar`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addEvent(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/events/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getEvent(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/events/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateEvent(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/events/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fullUpdateEvent(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/events/${queryParams.id}/update`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchEventStatus(ctx, queryParams) {
      const actionName = 'fetchEventStatus'
      const cachedData = getCachedData(actionName, queryParams, TTL.month)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/event-status/', queryParams)
          .then(response => {
            // ctx.commit('SET_EVENT_STATUSES', response.data)
            setCachedData(actionName, queryParams, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    copyEvent(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/events/${queryParams.id}/copy`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchEventsFromShows(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/eventsFromShows', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSectorsFromShows(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/sectorsFromShows', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchEventEasyList(ctx, queryParams) {
      const actionName = 'fetchEventEasyList'
      const cachedData = getCachedData(actionName, queryParams)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/eventEasyList', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    eventsListExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/eventsListExport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    eventShareToggle(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/events/${id}/share-toggle`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    eventCanDeleteQuota(ctx, { id, quotaId }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`events/${id}/can-delete-quota/${quotaId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Funds
    fetchFunds(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/funds/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addFund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/funds/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getFund(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/funds/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateFund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/funds/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Seats
    fetchSeats(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/seats/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addSeat(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/seats/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getSeat(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/seats/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSeatMarkTypes() {
      return new Promise((resolve, reject) => {
        axios
          .get('/seat-marks/types')
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSeatMarks(ctx, { seatId }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/seats/${seatId}/marks`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSeatMarksRegistry(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/seat-marks', { params: queryParams })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteSeatMark(ctx, { seatId, markId }) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/seats/${seatId}/marks/${markId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteEventSeatMark(ctx, { eventId, seatId, markId }) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/events/${eventId}/seats/${seatId}/marks/${markId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    syncSeatMarks(ctx, { seatId, marks }) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/seats/${seatId}/marks`, { marks })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchEventSeatMarksMap(ctx, { eventId }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/events/${eventId}/seat-marks`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchEventSeatMarks(ctx, { eventId, seatId }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/events/${eventId}/seats/${seatId}/marks`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    syncEventSeatMarks(ctx, { eventId, seatId, marks }) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/events/${eventId}/seats/${seatId}/marks`, { marks })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateSeat(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/seats/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    delSeat(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/seats/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Sectors
    fetchSectors(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/sectors/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addSector(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/sectors/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getSector(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/sectors/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateSector(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/sectors/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    delSector(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/sectors/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Companies
    fetchCompanies(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/companies/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addCompany(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/companies/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getCompany(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/companies/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateCompany(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/companies/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Banks
    fetchBanks(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/banks/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addBank(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/banks/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getBank(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/banks/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateBanks(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/banks/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // BankAccounts
    fetchBankAccounts(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bank-accounts/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addBankAccount(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/bank-accounts/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getBankAccount(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/bank-accounts/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateBankAccount(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/bank-accounts/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteBankAccount(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/bank-accounts/${queryParams.id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Benefit
    fetchBenefits(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/benefits/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addBenefit(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/benefits/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getBenefit(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/benefits/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateBenefit(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/benefits/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteBenefit(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/benefits/${queryParams.id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // TypesCharitiesCompany
    fetchTypesCharitiesCompanies(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/types-charities-companies/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addTypesCharitiesCompany(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/types-charities-companies/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getTypesCharitiesCompany(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/types-charities-companies/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateTypesCharitiesCompany(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/types-charities-companies/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteTypesCharitiesCompany(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/types-charities-companies/${queryParams.id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Cashboxes
    fetchCashboxes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/cashboxes/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    cashboxesList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/cashboxesList/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addCashbox(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/cashboxes/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getCashbox(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/cashboxes/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateCashbox(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/cashboxes/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Sellers
    fetchSellers(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/sellers/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // fetch Sellers Recipients
    fetchRecipients(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/recipients/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSellersEasy(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/fetchSellers/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    sellersListLight(ctx, queryParams) {
      const actionName = 'sellersListLight'
      const cachedData = getCachedData(actionName, queryParams, TTL.days(7))

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/sellersListLight/', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    addSeller(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/sellers/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getSeller(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/sellers/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateSeller(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/sellers/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSellersTypeList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/sellers/types/list', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchCommissionTypes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/sellers/commission-types', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Contacts
    fetchContacts(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/contacts/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addContact(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/contacts/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getContact(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/contacts/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateContact(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/contacts/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Orders
    fetchOrders(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/orders/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchOrdersLightweight(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/orders-lightweight', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/order/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/order/${queryParams.id}`, {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/order/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    copyOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/order/${queryParams.id}/copy-order`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    refundOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/order/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    removeSeatFromOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/order/${queryParams.id}/remove-seats`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    orderPaymentVerification(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/orders/${queryParams.orderId}/payment-verification`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    sendEmails(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/order/${queryParams.id}/send-emails`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Clients
    fetchClients(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/clients/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchClientsSummary(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/clients-summary', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchClientsSelect(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/clients-select', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addClient(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/client/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getClient(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/client/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getFullClient(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/client_full${id ? `/${id}` : ''}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateClient(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/client/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    attachPlaceToClient(ctx, {clientId, placeId}) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/client/${clientId}/place/${placeId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    detachPlaceFromClient(ctx, {clientId, placeId}) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/client/${clientId}/place/${placeId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // OrderRefund
    fetchOrderRefund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/refund/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addOrderRefund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/refund/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getOrderRefund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/refund/${queryParams.id}`, {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateOrderRefund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/refund/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    exportOrderRefund(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/refund/export', {
            params: queryParams,
            responseType: 'blob', // Важно для скачивания файла
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Tickets
    fetchTickets(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/tickets/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getTicket(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/ticket/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // addTicket(ctx, queryParams) {
    //   return new Promise((resolve, reject) => {
    //     axios
    //       .post('/ticket/', queryParams)
    //       .then(response => resolve(response))
    //       .catch(error => reject(error))
    //   })
    // },
    updateTicket(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/ticket/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    resetTicketScan(ctx, ticketId) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/ticket/${ticketId}/reset-scan`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchEventSeatsStatuses(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/event-seats/statuses', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    eventSeats(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/event-seats/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getEventSeat(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/event-seat/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // RentalPeriod
    fetchRentalPeriods(ctx, queryParams) {
      const actionName = 'fetchRentalPeriods'
      const cachedData = getCachedData(actionName, queryParams)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios
          .get('/rentalPeriods/', {params: queryParams})
          .then(response => {
            // ctx.commit('SET_RENTAL_PERIODS', response.data.data)
            setCachedData(actionName, queryParams, response.data.data)
            return resolve(response.data)
          })
          .catch(error => reject(error))
      })
    },
    // RentalPeriodList
    fetchRentalPeriodsList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/rentalPeriods/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    async fetchRentalPeriodsListShot(ctx, queryParams) {
      if (ctx.state.rentalPeriods !== null) {
        return {data: ctx.state.rentalPeriods}
      }
      try {
        const response = await axios.get('/rentalPeriodsShot/', {params: queryParams})
        ctx.commit('SET_RENTAL_PERIODS', response.data.data)
        return response.data
      } catch (error) {
        throw error
      }
    },
    async fetchRentalPeriodsListFull(ctx, queryParams) {
      if (ctx.state.rentalPeriods !== null) {
        return {data: ctx.state.rentalPeriods}
      }
      try {
        const response = await axios.get('/rentalPeriodsFull/', {params: queryParams})
        ctx.commit('SET_RENTAL_PERIODS', response.data.data)
        return response.data
      } catch (error) {
        throw error
      }
    },
    addRentalPeriod(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/rentalPeriod/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getRentalPeriod(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/rentalPeriod/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateRentalPeriod(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/rentalPeriod/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Reports
    m01ReportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M01ReportGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m01ReportGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M01ReportGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/BookingReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/BookingReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    ticketsReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/TicketsRep/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    ticketsReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/TicketsRep/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    k01ReportExportDetail(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/K01NewDetailNew/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    k01ReportDetail(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/K01NewDetailNew/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    k01DetailExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/K01Detail/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    k01Detail(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/K01Detail/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    k01ReportAgents(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/K01ReportAgents/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    k01ReportExportAgents(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/K01ReportAgents/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l05Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L05Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l05ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L05Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    eventsList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/eventsList/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    placesList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/placesList/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    sellersList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/sellersList/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    showsList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/showsList/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    companyForUser(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/companyForUser/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    reportListCompanies(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/reportListCompanies/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    clientSearch(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/clientSearch/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    gateYandex(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/gate-yandex/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    gateYandexExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/gate-yandex/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    reportListPlaces(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/reportListPlaces', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    reportListAgentsTP(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/reportListAgentsTP/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePrice(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/averagePrice/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePriceExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/averagePrice/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePrice_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/averagePrice/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePriceExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/averagePrice/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    dinamicsSaleses(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/dinamicsSaleses/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    dinamicsSalesesExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/dinamicsSaleses/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    dinamicsSaleses_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/dinamicsSaleses/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    dinamicsSalesesExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/dinamicsSaleses/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01EventReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T01EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01EventReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T01EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01OperReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T01OperReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01OperReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T01OperReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01EventReport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/T01EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01EventReportExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/T01EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01OperReport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/T01OperReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t01OperReportExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/T01OperReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t02Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T02Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m04Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M04ReportNew/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m04ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M04ReportNew/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m06ReportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M06ReportGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m06ReportGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M06ReportGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m07Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M07Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m07ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M07Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m07ReportExportDocx(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M07ReportDocx/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    printCheck(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/print-requests', {id: queryParams.id, type: queryParams.type})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePriceFact(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/AveragePriceFact/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePriceFactExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/AveragePriceFact/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePriceFact_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/AveragePriceFact/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    averagePriceFactExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/AveragePriceFact/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m02Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M02ReportChange/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m02ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M02ReportChange/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c01Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C01Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c01ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C01Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c02Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C02Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c02ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C02Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c03Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C03Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c03ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C03Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c03Report_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/C03Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c03ReportExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/C03Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c04Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C04Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c04ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C04Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    promoReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/PromoReportNew/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    promoReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/PromoReportNew/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    invReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/InvReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    invReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/InvReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    refundsReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/RefundsReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    refundsReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/RefundsReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    r01Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/R01Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    r01ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/R01Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    clients4ExportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/ClientsExportGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    clientsExportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/ClientsExportGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    testBoxes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/TestBoxes/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    printSeatsList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/printSeatsList', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    printBookedFull(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/printBookedFull', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    printBooked(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/printBooked', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    printInvoice(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/printInvoice', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    printInvoiceFull(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/printInvoiceFull', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c05Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C05Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c05ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C05Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c06Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C06Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c06ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C06Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c07Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C07Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c07ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C07Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c08Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C08Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c08ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C08Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c09Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C09Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c09ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C09Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    specTickets(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/specTicketsReport', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    specTicketsExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/specTicketsReport', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingSpecExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/BookingReport', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingSvodExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bookingSvod', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingSvod(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bookingSvod', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m05ReportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M05ReportGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m05ReportGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M05ReportGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    e01EventGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/E01EventGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    e01EventGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/E01EventGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    e01OperGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/E01OperGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    e01OperGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/E01OperGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    e02ReportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/E02ReportGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    e02ReportGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/E02ReportGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m04ReportGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M04ReportGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    m04ReportGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/M04ReportGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    circusesGate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/CircusesGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    circusesGateExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/CircusesGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    circusesGate_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/CircusesGate/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    circusesGateExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/CircusesGate/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    circusesAgent(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/CircusesAgent/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    circusesAgentExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/CircusesAgent/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    agentsTP(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/agentsTP/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    agentsTPExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/agentsTP/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c12Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/c12Plan/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c12ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/c12Plan/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchShowsWithCity(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/fetchShowsWithCity/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingCancell(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bookingCancell/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookingCancellExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bookingCancell/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getListPromocodes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/getListPromocodes/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Posters
    fetchPosters() {
      return new Promise((resolve, reject) => {
        axios
          .get('posters')
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Roles
    fetchRoles(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('roles', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getRoles(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('roles', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Promocodes
    fetchPromocodes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/promocodes/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addPromocode(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/promocodes/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getPromocode(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/promocodes/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updatePromocode(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/promocodes/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    delPromocode(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/promocodes/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    searchPromocode(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/promocode/search', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    searchPromocodes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/promocodes/search', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deletePromoCodeForSeats(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/deletePromoCodeForSeats', {...queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Programs
    fetchPrograms(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/programs/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addProgram(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/programs/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getProgram(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/programs/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateProgram(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/programs/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteProgram(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/programs/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Price Zones
    fetchPriceZones(ctx, queryParams) {
      if (ctx.state.priceZones !== null && (Object.keys(queryParams ?? {}).length === 0)) {
        return new Promise(resolve => resolve({data: {data: ctx.state.priceZones}}))
      }
      return new Promise((resolve, reject) => {
        axios
          .get('/price-zones/', {params: queryParams})
          .then(response => {
            ctx.commit('SET_PRICE_ZONES', response.data.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    getPriceZone(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/price-zones/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updatePriceZones(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/price-zones/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addPriceZone(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/price-zones/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deletePriceZone(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/price-zones/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    applyPromoCodeToSeats(ctx, {seats, promocode_id, order_id}) {
      return new Promise((resolve, reject) => {
        axios
          .post('/event-seats/applyPromoCode', {seats, promocode_id, order_id})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Tools
    fetchSelectOptions(ctx, {q, type, ...params}) {
      return new Promise((resolve, reject) => {
        axios
          .get('/tools/options', {params: {q, type, ...params}})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    // Получаем заказ по id места
    getOrderByEventSeatId(ctx, eventSeatId) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/getOrderByEventSeatId/${eventSeatId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateOrderField(ctx, {fieldName, queryParams}) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/updateOrderField/${fieldName}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем данные баллов пользователя
    fetchPointsByUserId(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/fetchPointsByUserId/${queryParams.userId}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Обновляем данные баллов пользователя
    updatePointsByUserId(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/updatePointsByUserId/${queryParams.userId}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем историю баллов пользователя
    fetchHistoryPointsByUserId(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/fetchHistoryPointsByUserId/${queryParams.userId}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем список статусов заказа
    fetchOrderStatuses(ctx, queryParams = {}) {
      const actionName = 'fetchOrderStatuses'
      const cachedData = getCachedData(actionName, queryParams, TTL.month)

      if (cachedData !== null) return new Promise(resolve => resolve({data: cachedData}))

      return new Promise((resolve, reject) => {
        axios.get('/order-status', {params: queryParams})
          .then(response => {
            setCachedData(actionName, queryParams, response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    // Получаем список льгот
    fetchBeneficiaries(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('beneficiaries', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем информацию о льготе по ID
    getBeneficiaryById(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/beneficiaries/${queryParams.id}`, {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Добавляем льготу
    beneficiaryStore(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/beneficiaries/store', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Обновляем льготу
    beneficiaryUpdate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/beneficiaries/update/${queryParams.beneficiary_id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем типы льгот
    fetchBeneficiaryTypes(ctx, queryParams) {
      // const actionName = 'fetchBeneficiaryTypes'
      // const cachedData = getCachedData(actionName, queryParams, TTL.month)
      //
      // if (cachedData !== null) return new Promise(resolve => resolve({ data: cachedData }))

      return new Promise((resolve, reject) => {
        axios
          .get('/beneficiaries/types', {params: queryParams})
          .then(response =>
            // setCachedData(actionName, queryParams, response.data)
            resolve(response))
          .catch(error => reject(error))
      })
    },
    // Добавляем льготу к заказу
    applyBeneficiaryForOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/beneficiaries/applyForOrder', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Удаляем льготу из заказа
    deleteBeneficiaryFromOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/beneficiaries/deleteFromOrder', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем информацию по системе лояльности для клиента по ID
    getLoyaltySystemDataByClientId(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/loyalty/client/${queryParams.clientId}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем информацию по льготе по ID
    fetchBeneficiaryDataById(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/loyalty/beneficiary/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // // Получаем информацию о льготнике по ID пользователя
    // fetchBeneficiaryInfoByUserId(ctx, queryParams) {
    //   return new Promise((resolve, reject) => {
    //     axios
    //       .post(`/fetchBeneficiaryInfoByUserId/${queryParams.userId}`, queryParams)
    //       .then(response => resolve(response))
    //       .catch(error => reject(error))
    //   })
    // },
    // Получаем операторов
    fetchOperators(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('fetchOperators', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем пользователей
    fetchUsers(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('users', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем пользователей
    userUnlocked(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`users/${queryParams.userId}/unlocked`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем пользователя по ID
    getUser(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/users/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Получаем настройки / Settings API
    fetchSettings(ctx, queryParams = {}) {
      return requestSharedSettings('crm', async () => {
        const response = await axios.get('fetchSettings', queryParams)
        return response.data.data
      }).then(settings => {
        ctx.commit('app/SET_SETTINGS', settings, { root: true })
        return settings
      })
    },
    fetchSettingsList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/settings/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSettingDoc(ctx, queryParams = {}) {
      return new Promise((resolve, reject) => {
        axios
          .get('settings-docs', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateSettingsByKey(ctx, {key, value}) {
      return new Promise((resolve, reject) => {
        axios.post(`/settings/${key}`, {value})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    uploadSettingsDoc(ctx, {slug, formData}) {
      return new Promise((resolve, reject) => {
        axios.post(`/settings/doc/${slug}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSettingRoles(ctx, queryParams = {}) {
      return new Promise((resolve, reject) => {
        axios
          .get('settings/roles', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchSettingPermissions(ctx, queryParams = {}) {
      return new Promise((resolve, reject) => {
        axios
          .get('settings/permissions', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    async updateRolePermission(_, {roleId, permissionId, action}) {
      return axios({
        method: 'post',
        url: `settings/roles/${roleId}/${permissionId}`,
        data: {action},
      })
    },
    // создатели заказов из ЦРМ
    listCreatersOrders(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/listCreatersOrders/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchClientTypeNames(ctx, queryParams = {}) {
      if (ctx.state.clientTypes !== null && (Object.keys(queryParams ?? {}).length === 0)) {
        return new Promise(resolve => resolve({data: ctx.state.clientTypes}))
      }
      return new Promise((resolve, reject) => {
        axios
          .get('/clients_type_names', {params: queryParams})
          .then(response => {
            ctx.commit('SET_CLIENT_TYPES', response.data)
            return resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    promoFilialExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/PromoFilial/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    promoFilial(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/PromoFilial/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    ticketsOut(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/TicketsOut/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    ticketsOutExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/TicketsOut/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    StatisticsHallNew(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/StatisticsHallNew/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    StatisticsHallShow(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/StatisticsHallNew/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    StatisticsHall(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/StatisticsHall/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    StatisticsHallFull(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/StatisticsHall/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    ShowHallPrint(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/ShowHallPrint/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    ShowHallEdit(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/ShowHallPrint/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c11Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C11Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c11ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C11Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c13Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C13Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c13ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C13Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c14Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C14Report', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c14ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C14Report', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l01EventReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L01EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l01EventReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L01EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l02EventReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L02EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l02EventReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L02EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l03EventReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L03EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l03EventReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L03EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l06Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L06Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l06ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L06Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l07Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L07Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l07ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L07Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // l02OperReport(ctx, queryParams) {
    //   return new Promise((resolve, reject) => {
    //     axios
    //       .get('/L02OperReport/', { params: queryParams })
    //       .then(response => resolve(response))
    //       .catch(error => reject(error))
    //   })
    // },
    // l02OperReportExport(ctx, queryParams) {
    //   return new Promise((resolve, reject) => {
    //     axios
    //       .get('/L02OperReport/', { params: queryParams, responseType: 'arraybuffer' })
    //       .then(response => resolve(response))
    //       .catch(error => reject(error))
    //   })
    // },
    c15Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/programsReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c15ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/programsReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c15Report_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/programsReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c15ReportExport_v1(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/v1/programsReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    analyticsReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/analyticsReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    audienceSurveyReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/reports/audience-survey', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    audienceSurveyReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/reports/audience-survey/export', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l04EventReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L04EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    l04EventReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/L04EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getPaymentByOrderId(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/payments/getPaymentByOrderId/${queryParams.orderId}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verificationAndCorrectionPaymentForOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/verificationAndCorrectionPaymentForOrder', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verificationAndCorrectionReceiptForPayment(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/verificationAndCorrectionReceiptForPayment', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    refundForServiceFee(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/refundForServiceFee', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verificationAndCorrectionAllPaymentForRefundOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/verificationAndCorrectionAllPaymentForRefundOrder', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verificationAndCorrectionPaymentForRefundOrder(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/verificationAndCorrectionPaymentForRefundOrder', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verificationAndCorrectionReceiptForRefundPayment(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/verificationAndCorrectionReceiptForRefundPayment', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verificationAndCorrectionReceiptReturnedServiceFeeAmount(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/verificationAndCorrectionReceiptReturnedServiceFeeAmount', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    creationReceiptForPayment(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/creationReceiptForPayment', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    creationReceiptForRefundPayment(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/payments/creationReceiptForRefundPayment', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    failPaymentsReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/FailPaymentsReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    failPaymentsReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/FailPaymentsReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    failReceiptsReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/FailReceiptsReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    failReceiptsReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/FailReceiptsReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t03Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T03Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    t03ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/T03Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c16Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C16Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c16ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C16Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c17Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C17Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c17ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C17Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    inv4eventReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/Inv4EventReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    inv4eventReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/Inv4EventReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c18Report(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C18Report/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    c18ReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/C18Report/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getDeal(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/deals/${queryParams.id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookedFizLicReport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bookedFizLicReport/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    bookedFizLicReportExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/bookedFizLicReport/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    editPayRequest(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/editPayRequest/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    editPayRequestExport(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/editPayRequest/', {params: queryParams, responseType: 'arraybuffer'})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Ticket Templates
    fetchTicketTemplates(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/ticket-templates/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addTicketTemplate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        Object.keys(queryParams).forEach(key => {
          const value = queryParams[key]
          if (value === null || value === undefined) {
            return
          }
          formData.append(
            key,
            value instanceof File || value instanceof Blob
              ? value
              : typeof value === 'object' && value !== null
                ? JSON.stringify(value)
                : value,
          )
        })

        axios
          .post('/ticket-templates/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateTicketTemplate(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        Object.keys(queryParams).forEach(key => {
          const value = queryParams[key]
          if (value === null || value === undefined) {
            return
          }
          formData.append(
            key,
            value instanceof File || value instanceof Blob
              ? value
              : typeof value === 'object' && value !== null
                ? JSON.stringify(value)
                : value,
          )
        })

        axios
          .post(`/ticket-templates/${queryParams.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    cloneTicketTemplate(ctx, id) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/ticket-templates/${id}/clone`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getTicketTemplate(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/ticket-templates/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getTicketTemplateViews(ctx) {
      return new Promise((resolve, reject) => {
        axios
          .get('/ticket-templates/views')
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Quotas
    fetchQuotas(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/quotas/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addQuota(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/quotas/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getQuota(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/quotas/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateQuota(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/quotas/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteQuota(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/quotas/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Type Leads
    fetchTypeLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/type-leads/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addTypeLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/type-leads/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateTypeLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/type-leads/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getTypeLead(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/type-leads/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Stage Leads
    fetchStageLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/stage-leads/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addStageLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/stage-leads/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateStageLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/stage-leads/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getStageLead(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/stage-leads/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Leads
    fetchLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/leads/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/leads/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateLeads(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/leads/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getLead(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/leads/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    // Show types
    fetchShowTypes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/show-types/', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchShowTypesSelect(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/show-types-select', {params: queryParams})
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    addShowType(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/show-types/', queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    getShowType(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/show-type/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    updateShowType(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/show-type/${queryParams.id}`, queryParams)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    deleteShowType(ctx, {id}) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/show-type/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
  },
}
